import { PlusIcon } from 'lucide-react';
import * as React from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';

import { DataTableFilterChip } from '../index-table/data-table-filter-chip';
import type { FilterDef, FilterValue, FilterValues } from '../index-table/types';

export interface ResourcePickerFiltersProps {
  defs: ReadonlyArray<FilterDef<never>>;
  values: FilterValues;
  onChange: (values: FilterValues) => void;
}

/**
 * The row under the picker's search: applied filters as chips ("Tags is Bridal"), a dashed
 * "Add filter" menu of the rest, and "Clear all". A filter picked from the menu shows as an
 * open chip until it gets a value; closing it empty drops it again.
 */
export function ResourcePickerFilters({ defs, values, onChange }: ResourcePickerFiltersProps) {
  const { t } = useTranslation('common');
  const [openKey, setOpenKey] = React.useState<string | null>(null);
  const shown = defs.filter((def) => values[def.key] !== undefined || def.key === openKey);
  const available = defs.filter((def) => values[def.key] === undefined && def.key !== openKey);
  const applied = Object.keys(values).length > 0;

  const set = (key: string, next: FilterValue | undefined) => {
    const { [key]: _dropped, ...rest } = values;
    onChange(next === undefined ? rest : { ...rest, [key]: next });
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {shown.map((def) => (
        <DataTableFilterChip
          key={def.key}
          def={def}
          value={values[def.key]}
          open={openKey === def.key}
          onOpenChange={(open) => setOpenKey(open ? def.key : null)}
          onChange={(next) => set(def.key, next)}
          onRemove={() => {
            set(def.key, undefined);
            setOpenKey(null);
          }}
        />
      ))}
      {available.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 border-dashed px-2 text-xs font-normal"
            >
              {t('resourcePicker.addFilter')}
              <PlusIcon aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" onCloseAutoFocus={(event) => event.preventDefault()}>
            {available.map((def) => (
              <DropdownMenuItem key={def.key} onSelect={() => setOpenKey(def.key)}>
                {def.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      {applied ? (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="text-muted-foreground h-7 px-1 text-xs"
          onClick={() => {
            onChange({});
            setOpenKey(null);
          }}
        >
          {t('resourcePicker.clearAll')}
        </Button>
      ) : null}
    </div>
  );
}
